import { startCase } from 'lodash';

import { AIJobFailure, jobFailure } from './AIJob';
import { NetworkStatus, StatusField } from './GroupPayerNetworkStatus';
import { patientNameFrom } from './patientName';

// Status of a single action (design §5/Appendix C). Phase 0's manual flow mostly
// exercises pending / action_required / completed.
export enum ActionStatus {
  PENDING = 'pending',
  FLAGGED = 'flagged',
  CANCELED = 'canceled',
  SKIPPED = 'skipped',
  ACTION_REQUIRED = 'action_required',
  RUNNING = 'running',
  COMPLETED = 'completed',
  ERRORED = 'errored',
}

// A branch central intake's decision can name, with the address that tells two of a name apart.
export interface BranchOption {
  id: string;
  name: string;
  address?: { streetAddress?: string | null; city?: string | null; state?: string | null; zip?: string | null };
}

// The groups (buildings) a run is addressed to. `groupType` carries the kind's
// api_name + display label (e.g. "SNF"); null when the group has no type.
export interface WorkflowGroupTypeOptions {
  apiName?: string;
  displayName?: string;
}

export interface WorkflowGroupOptions {
  id: string;
  name: string;
  groupType?: WorkflowGroupTypeOptions | null;
  // When THIS referral was sent to this building (the WorkflowGroup join's created_at), so the SNF
  // slideout's "Time to Decision" column has a genuine per-SNF value to render + sort on (OLIO-3240).
  // Present on `workflow.groups[]` (the referral's target SNFs); null on an action's own `group`,
  // which the API serves without it.
  sentAt?: string | Date | null;
}

// Normalize a group payload to a full shape — shared by workflow.groups[] (which carry `sentAt`) and
// each action's own `group` (which does not — sentAt falls back to null there).
export function toGroup(g: Partial<WorkflowGroupOptions>): WorkflowGroupOptions {
  return { id: g.id ?? '', name: g.name ?? '', groupType: g.groupType ?? null, sentAt: g.sentAt ?? null };
}

// The declared input snapshot an action ran with (P1-1). An array (not a map) so
// `name` strings stay snake_case and line up with values[].name for the diff.
export interface WorkflowInputOptions {
  name: string;
  value: string | null;
}

// A STAGED output — recorded on the action via save_progress (or a paused machine
// retrieval) but not yet committed to the workflow's values. Values may be structured
// (the attachments packet is an array of {id, filename}).
export interface WorkflowActionOutputOptions {
  name: string;
  value: unknown;
  label?: string;
  // Whether this viewer can open the workflow this output NAMES — set by the API only on outputs
  // whose value is a bare workflow id (OLIO-3382). Rides on the entry rather than the value
  // because the value is a plain id string every reader already narrows with `typeof`.
  viewable?: boolean;
}

// A facility decision row's network status for the payer picked for the primary insurance,
// recomputed on every payload. Null (or absent from an older API) when the feature is off or no
// payer is picked yet: show nothing.
export type GroupPayerNetworkStatusOnAction = {
  stediPayerId: string | null;
  stediPayerName: string | null;
  configured: boolean;
} & Record<StatusField, NetworkStatus>;

export type WorkflowActionDismissal = {
  dismissedByName: string | null;
  dismissedAt: string;
};

export interface WorkflowActionOptions {
  id: string;
  actionType: string;
  status: ActionStatus;
  // The status the API says the FE should PRESENT (label/tone/icon) — the kind's derived view of
  // `status` (e.g. a completed DenialCheck match surfaces as `flagged`). Defaults to `status`.
  // Behavior (isActionRequired/isCompleted/isFlagged, terminality) keys off `status`, never this.
  effectiveStatus: ActionStatus;
  categoryKey: string | null;
  position: number | null;
  groupId: string | null;
  // The building this action is fanned onto, served alongside groupId so consumers read the
  // group's name/type off the action directly (no lookup into workflow.groups). Null for
  // shared/ungrouped actions.
  group: WorkflowGroupOptions | null;
  // The coverage this action is fanned onto (design 008 §3e) — the second fan-out dimension,
  // beside the building one. Null for referral-level actions and for every action on a referral
  // whose type declares no dimension. Unlike `group` there's no object served alongside: the
  // coverage's label lives in workflow.facets, because it's the tab strip that needs it.
  facetId: string | null;
  // A re-run instance (chained to a prior one) — the API serves previous_id presence.
  rerun: boolean;
  // Whether a person may Re-run this instance. False for a benefits check documented by hand.
  // Absent from older payloads, which reads as true.
  rerunnable: boolean;
  // Whether "Send documents to existing record" (or its Retry) is on offer for this instance — the api
  // answers it from the same checks the send enforces. Only a Sync Patient to EHR step is ever true.
  sendableToExistingRecord?: boolean;
  // Central intake's decision step only: the branches it can name, and the sender's own denial reasons.
  branchOptions?: BranchOption[];
  denialReasonOptions?: { apiName: string; displayName: string }[];
  // Whether Dismiss is on offer for this benefit verification — a flagged check on a secondary coverage,
  // on a referral not yet decided. The api answers it from the same checks the dismissal enforces; only
  // a benefits check is ever true.
  dismissible?: boolean;
  // Who dismissed this benefit verification and when; null unless it was dismissed.
  dismissal?: WorkflowActionDismissal | null;
  // P1-2 flagged axis: the declared value names the engine couldn't derive, so this
  // action can't run until a person provides them. Empty unless status is `flagged`.
  unobtainableValues: string[];
  inputs: WorkflowInputOptions[];
  // The action's per-execution result (design §16). While action_required it is the evidence a
  // reviewer acts on before completion (e.g. PayerSearch's payer_candidates, a recoverable
  // benefits_eligibility_result, portal retrieval's staged values). It persists once the action
  // completes, and some surfaces read it there: Patient Demographics compares its outputs — what
  // the packet said — with the values in use (OLIO-3252). Same array shape as inputs so names stay
  // snake_case; label rides along for the confirm panel's display rows.
  outputs: WorkflowActionOutputOptions[];
  // Display + value-graph contract, served by the API (P1-5 / OLIO-2574) — the FE no longer
  // hardcodes these. `label`/`description` are the definition's mutable display copy; `required`/
  // `produces` are the value names this action consumes/produces.
  label: string | null;
  description: string | null;
  required: string[];
  // The subset of `required` whose later change makes a COMPLETED result untrustworthy
  // (OLIO-2613). `required` answers "what is this step waiting for"; this answers "what would
  // make its answer wrong". A step can need a value without being invalidated by it: the clinical
  // assessment waits on the duplicate-patient result so it doesn't burn an AI call on a referral
  // about to be canceled, but it reads documents, so re-running that search cannot change its
  // verdict. Absent (older payloads, other endpoints) means every required input invalidates,
  // which is how everything behaved before this existed.
  invalidatedBy: string[];
  produces: string[];
  // Completion attribution ("Updated by <name> on <date>", OLIO-2611) — null until completed.
  completedAt: string | null;
  completedByName: string | null;
  // View attribution ("Viewed by <name> on <date>") — null until someone views the results.
  lastViewedByName: string | null;
  lastViewedAt: string | null;
  viewsCount: number;
  // Unread-note indicator: this facility's note thread holds a note the viewer has never been
  // shown, that isn't theirs. Drives the row icon and the Notes tab-label icon.
  hasNewActivities: boolean;
  // INDEX payload only: whether a capture sits staged on this action awaiting confirmation.
  // The list deliberately omits `outputs` (staged captures carry document arrays), so this
  // flag is how a list row can tell a capture-less retrieval from a captured one — the
  // review-vs-login card copy. The show payload carries the outputs themselves instead.
  hasStagedOutputs?: boolean;
  // INDEX payload only, beside hasStagedOutputs: the staged capture's patient name as one
  // server-derived scalar (the list still ships no outputs). Null when nothing real is staged
  // or the capture holds no name (a documents-only sweep).
  stagedPatientName?: string | null;
  groupPayerNetworkStatus?: GroupPayerNetworkStatusOnAction | null;
  // The permission gating this kind's acting intents, or null when the kind has none of its own
  // and rides workflow-level edit (OLIO-2477/2615). A snake_case name: OliveBranch camelizes
  // payload keys, not values, so read it through useWorkflowPermissions rather than comparing it
  // to a PERMISSIONS entry directly.
  editPermissionName: string | null;
}

export class WorkflowAction {
  id: string;
  actionType: string;
  status: ActionStatus;
  effectiveStatus: ActionStatus;
  categoryKey: string | null;
  position: number | null;
  groupId: string | null;
  group: WorkflowGroupOptions | null;
  facetId: string | null;
  rerun: boolean;
  rerunnable: boolean;
  sendableToExistingRecord: boolean;
  branchOptions: BranchOption[];
  denialReasonOptions: { apiName: string; displayName: string }[];
  dismissible: boolean;
  dismissal: WorkflowActionDismissal | null;
  unobtainableValues: string[];
  inputs: WorkflowInputOptions[];
  outputs: WorkflowActionOutputOptions[];
  label: string | null;
  description: string | null;
  required: string[];
  invalidatedBy: string[];
  produces: string[];
  completedAt: string | null;
  completedByName: string | null;
  lastViewedByName: string | null;
  lastViewedAt: string | null;
  viewsCount: number;
  hasNewActivities: boolean;
  hasStagedOutputs?: boolean;
  stagedPatientName?: string | null;
  groupPayerNetworkStatus: GroupPayerNetworkStatusOnAction | null;
  editPermissionName: string | null;

  constructor(opts: Partial<WorkflowActionOptions> = {}) {
    this.id = opts.id ?? '';
    this.actionType = opts.actionType ?? '';
    this.status = opts.status ?? ActionStatus.PENDING;
    // Falls back to the actual status when the API doesn't send one (older payloads / tests).
    this.effectiveStatus = opts.effectiveStatus ?? this.status;
    this.categoryKey = opts.categoryKey ?? null;
    this.position = opts.position ?? null;
    this.groupId = opts.groupId ?? null;
    this.group = opts.group ? toGroup(opts.group) : null;
    this.facetId = opts.facetId ?? null;
    this.rerun = opts.rerun ?? false;
    this.rerunnable = opts.rerunnable ?? true;
    this.sendableToExistingRecord = opts.sendableToExistingRecord ?? false;
    this.branchOptions = opts.branchOptions ?? [];
    this.denialReasonOptions = opts.denialReasonOptions ?? [];
    this.dismissible = opts.dismissible ?? false;
    this.dismissal = opts.dismissal ?? null;
    this.unobtainableValues = opts.unobtainableValues ?? [];
    this.inputs = (opts.inputs ?? []).map((i) => ({ name: i.name, value: i.value ?? null }));
    this.outputs = (opts.outputs ?? []).map((o) => ({
      name: o.name,
      value: o.value ?? null,
      label: o.label,
      viewable: o.viewable,
    }));
    this.label = opts.label ?? null;
    this.description = opts.description ?? null;
    this.required = opts.required ?? [];
    // Defaults to the full required list, not []: an empty array would mean "nothing invalidates
    // this", so a payload without the field would silently stop every correction cascade.
    this.invalidatedBy = opts.invalidatedBy ?? opts.required ?? [];
    this.produces = opts.produces ?? [];
    this.completedAt = opts.completedAt ?? null;
    this.completedByName = opts.completedByName ?? null;
    this.lastViewedByName = opts.lastViewedByName ?? null;
    this.lastViewedAt = opts.lastViewedAt ?? null;
    this.viewsCount = opts.viewsCount ?? 0;
    // Defaults false so a payload from before the API shipped this field renders no icon.
    this.hasNewActivities = opts.hasNewActivities ?? false;
    this.hasStagedOutputs = opts.hasStagedOutputs;
    this.stagedPatientName = opts.stagedPatientName;
    this.groupPayerNetworkStatus = opts.groupPayerNetworkStatus ?? null;
    this.editPermissionName = opts.editPermissionName ?? null;
  }

  // Whether a capture is parked here awaiting confirmation, answered from whichever payload
  // this action came off: the index's flag, or the show payload's staged outputs themselves.
  get hasStagedCapture(): boolean {
    return this.hasStagedOutputs ?? this.stagedValueOutputs.length > 0;
  }

  // The staged capture's patient name, answered from whichever payload this action came off:
  // the index's derived scalar, or composed the same way from the show payload's staged
  // outputs (stagedOutputs already excludes seeded_defaults, so a prefilled name never answers).
  get stagedCaptureName(): string {
    if (this.stagedPatientName != null) return this.stagedPatientName.trim();
    const staged = this.stagedOutputs();
    return patientNameFrom(staged.patient_first_name, staged.patient_middle_name, staged.patient_last_name);
  }

  // The uncommitted output recorded for this action while action_required (raw content — an
  // object/array for structured outputs). Read as unknown; callers narrow it.
  outputFor(name: string): unknown {
    return this.outputs.find((o) => o.name === name)?.value ?? undefined;
  }

  // Can this viewer open the workflow the named output points at? `undefined` when the API said
  // nothing — an older payload, or an output that names no workflow — which readers must treat as
  // viewable so a link never disappears just because the API half hasn't shipped yet.
  outputViewable(name: string): boolean | undefined {
    return this.outputs.find((o) => o.name === name)?.viewable;
  }

  get isRunning(): boolean {
    return this.status === ActionStatus.RUNNING;
  }

  get isErrored(): boolean {
    return this.status === ActionStatus.ERRORED;
  }

  // Why an errored run failed, in the AI job shape. Mirrors the api's WorkflowAction::FAILURE_OUTPUT.
  static readonly FAILURE_OUTPUT = 'failure';

  // What the run's creator already knew, staged at creation for a person to confirm
  // (OLIO-3076) — the FHIR task's values or the sweep row's fields, under the names a
  // retrieval collects them by. Mirrors the api's WorkflowAction::SEEDED_DEFAULTS_OUTPUT.
  static readonly SEEDED_DEFAULTS_OUTPUT = 'seeded_defaults';

  // Outputs that are NOT a captured value: evidence the platform recorded ABOUT a run (the
  // headless failure reason, OLIO-2888, and the AI job failure, both recorded on errored actions)
  // and the creation-time defaults. All are kept out of stagedValueOutputs — an errored action
  // carrying only a reason, or a new run carrying only prefills, must not read as "has staged values".
  static readonly EVIDENCE_OUTPUTS = new Set([
    'headless_failure_reason',
    WorkflowAction.SEEDED_DEFAULTS_OUTPUT,
    WorkflowAction.FAILURE_OUTPUT,
  ]);

  get failure(): AIJobFailure | null {
    return this.isErrored ? jobFailure(this.outputFor(WorkflowAction.FAILURE_OUTPUT)) : null;
  }

  // A failure with no code predates them, and keeps the Try again it always had.
  get canRetry(): boolean {
    return this.failure?.retryable ?? true;
  }

  // The staged captured values (evidence outputs excluded) — what the confirm panel
  // shows and what a confirm completes with.
  get stagedValueOutputs(): WorkflowActionOutputOptions[] {
    return this.outputs.filter((o) => !WorkflowAction.EVIDENCE_OUTPUTS.has(o.name));
  }

  // A creation-time default for one output name, or undefined. Deliberately NOT part of
  // stagedOutputs(): a confirm spreads the staged set back verbatim, and a default has to
  // travel through the form — where a person can see it and overwrite it — instead.
  //
  // Rows, not a keyed object, for the reason `outputs` itself is an array: OliveBranch
  // camelCases response keys, so an object would arrive as `patientDob` while every field
  // name matched against it is snake_case.
  defaultFor(name: string): unknown {
    const rows = this.outputFor(WorkflowAction.SEEDED_DEFAULTS_OUTPUT);
    if (!Array.isArray(rows)) return undefined;

    return (rows as WorkflowActionOutputOptions[]).find((row) => row?.name === name)?.value;
  }

  // The staged (uncommitted) outputs as a name -> value map, ready to send back as a
  // `complete` body once a person confirms them.
  stagedOutputs(): Record<string, unknown> {
    return Object.fromEntries(this.stagedValueOutputs.map((o) => [o.name, o.value]));
  }

  // `WorkflowActionDefinition::ClinicalReview` -> `ClinicalReview`
  get kind(): string {
    return this.actionType.split('::').pop() ?? this.actionType;
  }

  // A readable fallback label derived from the kind (`ClinicalReview` -> `Clinical Review`).
  get defaultLabel(): string {
    return startCase(this.kind);
  }

  // The label to render: the API's display copy, else the humanized kind. The FE no longer
  // carries hardcoded per-kind labels (OLIO-2574).
  get displayLabel(): string {
    return this.label ?? this.defaultLabel;
  }

  // A person must open this card and act on it now.
  get isActionRequired(): boolean {
    return this.status === ActionStatus.ACTION_REQUIRED;
  }

  get isFlagged(): boolean {
    return this.status === ActionStatus.FLAGGED;
  }

  get isCompleted(): boolean {
    return this.status === ActionStatus.COMPLETED;
  }

  // Not yet anyone's problem — render it disabled, never hide it (design §12).
  get isPending(): boolean {
    return this.status === ActionStatus.PENDING;
  }
}
